import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getNotificationPreferences,
  updateNotificationPreferences,
  DEFAULT_NOTIFICATION_PREFERENCES,
  type NotificationPreferences,
} from '@/api/notifications';

const PREFERENCES_KEY = ['notification-preferences'] as const;

export function useNotificationPreferences() {
  return useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: getNotificationPreferences,
    staleTime: 5 * 60 * 1000,
    placeholderData: DEFAULT_NOTIFICATION_PREFERENCES,
  });
}

/** Optimistic toggle — the switch flips instantly, rolls back on error. */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: Partial<NotificationPreferences>) => updateNotificationPreferences(update),
    onMutate: async (update) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_KEY });
      const previous = queryClient.getQueryData<NotificationPreferences>(PREFERENCES_KEY);
      queryClient.setQueryData<NotificationPreferences>(PREFERENCES_KEY, (old) => ({
        ...DEFAULT_NOTIFICATION_PREFERENCES,
        ...old,
        ...update,
      }));
      return { previous };
    },
    onError: (_err, _update, context) => {
      if (context?.previous) queryClient.setQueryData(PREFERENCES_KEY, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY });
    },
  });
}
